import type {
  Assignment,
  AuthUser,
  Batch,
  BootstrapData,
  CsrfToken,
  Dashboard,
  IngestReceipt,
  IngestRequest,
  MeasurementPoint,
  Pallet,
  PressureFrame,
  Sensor,
  TransportEvent,
} from '../types/domain'

const API_BASE = import.meta.env.VITE_API_URL ?? '/api/v1'
let csrfToken: CsrfToken | undefined

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly correlationId?: string,
  ) {
    super(message)
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase()
  const csrf = !['GET', 'HEAD', 'OPTIONS'].includes(method) ? await ensureCsrf() : undefined
  return rawRequest<T>(path, {
    ...init,
    headers: {
      ...(csrf?.enabled && csrf.headerName && csrf.token ? { [csrf.headerName]: csrf.token } : {}),
      ...init?.headers,
    },
  })
}

async function rawRequest<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...init,
      credentials: 'same-origin',
      cache: init?.method && init.method !== 'GET' ? init.cache : 'no-store',
      headers: {
        Accept: 'application/json',
        ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
        ...init?.headers,
      },
    })
  } catch (error) {
    throw new ApiError(error instanceof Error && error.name === 'AbortError'
      ? 'Сервер не ответил вовремя'
      : 'Нет соединения с backend. Проверьте, что сервис запущен.', 0)
  }
  if (!response.ok) {
    const problem = await response.json().catch(() => null) as { detail?: string; title?: string; message?: string } | null
    if (response.status === 401 && path !== '/auth/login' && path !== '/auth/me') {
      window.dispatchEvent(new CustomEvent('novaroll:unauthorized'))
    }
    throw new ApiError(
      problem?.detail ?? problem?.message ?? problem?.title ?? `Сервер вернул ошибку ${response.status}`,
      response.status,
      response.headers.get('X-Correlation-Id') ?? undefined,
    )
  }
  return response.json() as Promise<T>
}

async function ensureCsrf(): Promise<CsrfToken> {
  csrfToken ??= await rawRequest<CsrfToken>('/auth/csrf')
  return csrfToken
}

export const api = {
  me: () => request<AuthUser>('/auth/me'),
  csrf: () => ensureCsrf(),
  login: async (username: string, password: string) => {
    const token = await ensureCsrf()
    const user = await rawRequest<AuthUser>('/auth/login', {
      method: 'POST',
      body: new URLSearchParams({ username, password }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        ...(token.enabled && token.headerName && token.token ? { [token.headerName]: token.token } : {}),
      },
    })
    csrfToken = undefined
    await ensureCsrf()
    return user
  },
  logout: async () => {
    await request<{ status: string }>('/auth/logout', { method: 'POST' })
    csrfToken = undefined
  },
  dashboard: () => request<Dashboard>('/dashboard'),
  batches: () => request<Batch[]>('/batches'),
  pallets: () => request<Pallet[]>('/pallets'),
  sensors: () => request<Sensor[]>('/sensors'),
  transportEvents: () => request<TransportEvent[]>('/transport-events'),
  assignments: (palletId: string) => request<Assignment[]>(`/pallets/${palletId}/assignments`),
  series: (palletId: string) => request<MeasurementPoint[]>(`/measurements/series?palletId=${encodeURIComponent(palletId)}&maxPoints=5000`),
  pressureFrame: (palletId: string) => request<PressureFrame>(`/measurements/pressure-frame?palletId=${encodeURIComponent(palletId)}`),
  ingest: (payload: IngestRequest) => request<IngestReceipt>('/measurements/ingest', {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: { 'Idempotency-Key': payload.operationId },
  }),
  bootstrap: async (): Promise<BootstrapData> => {
    const [dashboard, batches, pallets, sensors, transportEvents] = await Promise.all([
      api.dashboard(), api.batches(), api.pallets(), api.sensors(), api.transportEvents(),
    ])
    return { dashboard, batches, pallets, sensors, transportEvents }
  },
}
